import React from 'react';
import { FaCalendarAlt, FaCalendarPlus, FaPlus, FaMinus } from 'react-icons/fa';
import { useSubscriptionCart } from '../../context/SubscriptionCartContext';
import {
  getSubscriptionCartLineTotal,
  subscriptionCartLineKey,
  suggestSlotIdForMenuItem,
} from '../../utils/subscriptionCartItems';
import { applyAddOnsToCartLine } from './cartAddOnUtils';
import {
  buildCartLineFromVariant,
  isVariableMenuItem,
  menuItemNeedsDetailBeforeAdd,
} from './ourMenuUtils';

const ADD_LABEL = {
  card: ' ADD TO PLAN',
  list: ' ADD TO PLAN',
  color: ' ADD TO PLAN',
  compact: '',
  modal: ' ADD TO PLAN',
};

/**
 * Add a menu item to the subscription cart as Regular Our menu (à la carte).
 * Options and/or add-ons open the details sheet unless already resolved in the modal.
 * When `editPlanLineKey` is set, updates that plan line in place (builder Edit).
 */
export default function MenuPlanAction({
  item,
  orderable = true,
  variant = 'card',
  className = '',
  onOpenOptions,
  selectedVariant = null,
  selectedAddOns = [],
  /** Host plate qty from details modal (plan). */
  draftQuantity = 1,
  /** When set, update this subscription-cart line instead of adding. */
  editPlanLineKey = null,
  /** Preserve meal slot when editing from the builder. */
  editSlotId = null,
  onAfterAdd,
}) {
  const {
    subscriptionCartItems,
    addToSubscriptionCart,
    updateSubscriptionCartQty,
    replaceSubscriptionCartLine,
  } = useSubscriptionCart();

  const variable = isVariableMenuItem(item);
  const isEditMode = Boolean(editPlanLineKey);
  const needsDetail =
    !isEditMode &&
    typeof onOpenOptions === 'function' &&
    menuItemNeedsDetailBeforeAdd(item, { selectedVariant });
  const onModal = variant === 'modal';
  const plateQty = Math.max(1, Math.round(Number(draftQuantity) || 1));

  const basePayload = selectedVariant
    ? buildCartLineFromVariant(item, selectedVariant)
    : variable
      ? null
      : {
          id: item.id,
          name: item.name,
          price: item.price,
          priceDisplay: item.priceDisplay,
          image: item.image,
          category: item.category,
        };

  const withAddOns = basePayload
    ? applyAddOnsToCartLine(
        {
          ...basePayload,
          hostUnitPrice:
            basePayload.hostUnitPrice != null
              ? basePayload.hostUnitPrice
              : basePayload.price,
          price:
            basePayload.hostUnitPrice != null
              ? basePayload.hostUnitPrice
              : basePayload.price,
        },
        selectedAddOns,
      )
    : null;

  const slotId = editSlotId
    || (withAddOns ? suggestSlotIdForMenuItem({ ...item, ...withAddOns }) : null);
  const payload = withAddOns
    ? {
        ...withAddOns,
        slotId,
        kind: 'alacarte',
      }
    : null;
  const lineKey = payload ? subscriptionCartLineKey(payload) : null;
  const planLine =
    lineKey != null
      ? (subscriptionCartItems || []).find(
          (row) => subscriptionCartLineKey(row) === lineKey,
        )
      : null;
  const quantity = Math.max(0, Number(planLine?.quantity) || 0);

  if (!orderable) return null;

  if (needsDetail) {
    return (
      <button
        type="button"
        className={`${className} menu-plan-btn`.trim()}
        onClick={() => onOpenOptions(item, { intent: 'plan' })}
        aria-label={`Add ${item.name} to plan — open details`}
        title="Add to meal plan — open details"
      >
        <FaCalendarPlus />
        {ADD_LABEL[variant] ?? ADD_LABEL.card}
      </button>
    );
  }

  if (!payload) return null;

  const commitAdd = () => {
    addToSubscriptionCart(payload, { quantity: onModal ? plateQty : 1 });
    if (typeof onAfterAdd === 'function') onAfterAdd();
  };

  const saveEdit = () => {
    if (!editPlanLineKey) return;
    replaceSubscriptionCartLine(editPlanLineKey, payload, {
      quantity: plateQty,
      slotId,
    });
    if (typeof onAfterAdd === 'function') onAfterAdd();
  };

  if (isEditMode && onModal) {
    const modalTotal = getSubscriptionCartLineTotal({ ...payload, quantity: plateQty });
    return (
      <button
        type="button"
        className={`${className} menu-plan-btn`.trim()}
        onClick={saveEdit}
        aria-label={`Update ${item.name} on meal plan`}
        title="Update meal plan item"
      >
        <FaCalendarPlus />
        {` UPDATE PLAN · ₹${modalTotal.toFixed(0)}`}
      </button>
    );
  }

  if (!onModal && quantity > 0) {
    return (
      <div
        className={`menu-qty-stepper menu-qty-stepper--plan menu-qty-stepper--${variant}`}
        role="group"
        aria-label={`Meal plan quantity for ${item.name}`}
        title="Meal plan"
      >
        <button
          type="button"
          className="menu-qty-stepper__btn"
          onClick={() =>
            updateSubscriptionCartQty(payload.id, slotId, quantity - 1, {
              lineKey,
            })
          }
          aria-label={`Decrease meal plan quantity of ${item.name}`}
        >
          <FaMinus />
        </button>
        <span className="menu-qty-stepper__value" aria-live="polite">
          <span className="menu-qty-stepper__kind">
            <FaCalendarAlt aria-hidden />
            <span className="menu-qty-stepper__kind-text">Plan</span>
          </span>
          <span className="menu-qty-stepper__count">{quantity}</span>
        </span>
        <button
          type="button"
          className="menu-qty-stepper__btn"
          onClick={() =>
            updateSubscriptionCartQty(payload.id, slotId, quantity + 1, {
              lineKey,
            })
          }
          aria-label={`Increase meal plan quantity of ${item.name}`}
        >
          <FaPlus />
        </button>
      </div>
    );
  }

  const modalTotal =
    onModal && payload
      ? getSubscriptionCartLineTotal({ ...payload, quantity: plateQty })
      : null;

  return (
    <button
      type="button"
      className={`${className} menu-plan-btn`.trim()}
      onClick={commitAdd}
      aria-label={`Add ${item.name} to subscription cart`}
      title="Add to subscription as Our menu"
    >
      <FaCalendarPlus />
      {onModal && modalTotal != null
        ? ` ADD TO PLAN · ₹${modalTotal.toFixed(0)}`
        : ADD_LABEL[variant] ?? ADD_LABEL.card}
    </button>
  );
}
